import { useState } from 'react';
import { Link } from 'react-router-dom';
import { resolveImagePath } from '@/utils/imageMap';
import { Navigation } from './Navigation';
import navigationData from '@/data/navigation.json';

export function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const logoUrl = resolveImagePath('/images/minuteman-logo.png');
  const targetIconUrl = resolveImagePath('/images/target-icon.png');
  
  return (
    <>
      {/* Main Header with logo and navigation */}
      <header id="masthead" className="site-header relative" style={{ backgroundColor: '#3a3a3a' }}>
        <div className="max-w-7xl mx-auto px-4">
          {/* Desktop header layout */}
          <div className="hidden lg:flex items-center justify-between py-4">
            {/* Large logo positioned to span both header sections */}
            <div className="absolute left-4 top-4 z-10">
              <Link to="/" className="block">
                <img 
                  src={logoUrl} 
                  alt="Minute Man Sportsman's Club" 
                  className="h-32 w-auto"
                />
              </Link>
            </div>
            
            {/* Navigation menu positioned to the right with target icon */}
            <div className="flex-1 ml-40 flex items-start justify-end">
              <div className="relative">
                {/* Target icon positioned above the first menu item (Home) - hidden on mobile */}
                <div className="hidden lg:block absolute top-0 left-0 flex justify-center" style={{ width: '80px', marginTop: '-18px', marginLeft: '2px' }}>
                  <img 
                    src={targetIconUrl} 
                    alt="Target Icon" 
                    className="w-5 h-5"
                  />
                </div>
                <Navigation 
                  items={navigationData.main} 
                  isMobile={false}
                  className=""
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Mobile menu button and logo */}
        <div className="lg:hidden flex items-center justify-between px-4 py-3 text-white">
          <button
            className="menu-icon focus:outline-none"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <svg className="w-6 h-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
              <path d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}></path>
            </svg>
          </button>
          <div className="title-bar-title">
            <Link to="/" className="text-white font-semibold">
              Minute Man Sportsman's Club
            </Link>
          </div>
        </div>
        
        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-slate-700 absolute left-0 right-0 top-full z-50 shadow-lg">
            <Navigation 
              items={navigationData.main} 
              isMobile={true}
              onItemClick={() => setIsMobileMenuOpen(false)}
            />
          </div>
        )}
      </header>
      
      {/* Sub-header with tagline and contact info */}
      <div className="sub-header relative" style={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="hidden lg:flex items-center justify-between py-4" style={{ marginLeft: '140px' }}>
            
            {/* Center tagline section */}
            <div className="flex-1 text-center px-8">
              <h2 className="text-sm font-medium leading-tight max-w-md mx-auto" style={{ color: '#3a3a3a' }}>
                MINUTE MAN SPORTSMAN'S CLUB IS ONE OF THE FINEST PRIVATELY OWNED SHOTGUN 
                ONLY FACILITIES ON THE EAST COAST
              </h2>
            </div>
            
            {/* Contact info on the right with social icons above */}
            <div className="text-right">
              {/* Social icons above phone number */}
              <div className="flex justify-end space-x-3 mb-2">
                {navigationData.social.map((social) => (
                  <a
                    key={social.platform}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-blue-600 transition-colors"
                    style={{ color: '#3a3a3a' }}
                    aria-label={`Visit our ${social.platform}`}
                  >
                    <SocialIcon platform={social.platform} />
                  </a>
                ))}
              </div>
              
              {/* Phone and hours */}
              <div className="text-sm space-y-1">
                <div className="font-bold text-lg">
                  <a href={`tel:${navigationData.contact.phone}`} className="hover:text-blue-600 transition-colors" style={{ color: '#3a3a3a' }}>
                    {navigationData.contact.phone}
                  </a>
                </div>
                {navigationData.contact.hours.map((hour, index) => (
                  <div key={index} className="text-xs" style={{ color: '#3a3a3a' }}>
                    {hour}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Social icon component
function SocialIcon({ platform }: { platform: string }) {
  const iconClass = "w-5 h-5";
  
  switch (platform) {
    case 'facebook':
      return (
        <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      );
    case 'instagram':
      return (
        <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      );
    case 'twitter':
      return (
        <svg className={iconClass} fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
        </svg>
      );
    default:
      return <div className={iconClass} />;
  }
}